import React, { useState, useRef, useEffect } from 'react';
import './AudioRecorder.css';

/**
 * Универсальный рекордер для аудио/видео (по умолчанию аудио).
 * mode: "audio" | "video"
 */
const AudioRecorder = ({ onRecordingComplete, minDuration = 10, maxDuration = 120, mode = 'audio' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const isVideo = mode === 'video';
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const finalDurationRef = useRef(0);
  const previewVideoRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      setError(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      setRecordingDuration(0);
      const constraints = isVideo ? { audio: true, video: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (isVideo && previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        previewVideoRef.current.muted = true;
        previewVideoRef.current.play().catch(() => {});
      }
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const finalDuration = finalDurationRef.current || recordingTime;
        const mimeType = isVideo ? 'video/webm' : 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(url);
        setRecordingDuration(finalDuration);
        
        // Останавливаем все треки потока
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = null;
        }
        
        if (onRecordingComplete) {
          const isValid = finalDuration >= minDuration;
          onRecordingComplete(audioBlob, isValid);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Таймер для отсчета времени
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          finalDurationRef.current = newTime;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (err) {
      setError('Не удалось получить доступ к микрофону. Проверьте разрешения.');
      console.error('Error accessing microphone:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };
  
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  
  const handleRerecord = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setRecordingDuration(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getButtonText = () => {
    if (audioUrl) {
      return 'Перезаписать';
    }
    if (isRecording) {
      return 'Остановить запись';
    }
    return isVideo ? 'Начать видеозапись' : 'Начать запись';
  };

  const getButtonClass = () => {
    if (audioUrl) {
      return 'btn-record-rerecord';
    }
    if (isRecording) {
      return 'btn-record-stop';
    }
    return 'btn-record-start';
  };

  const handleButtonClick = () => {
    if (audioUrl) {
      handleRerecord();
    } else {
      toggleRecording();
    }
  };

  return (
    <div className="audio-recorder">
      {error && <div className="audio-recorder-error">{error}</div>}

      <div className="media-shell">
        {isVideo ? (
          <video
            ref={previewVideoRef}
            className="media-player video-player"
            controls={!!audioUrl}
            autoPlay
            muted
            playsInline
            src={audioUrl || undefined}
          />
        ) : (
          audioUrl ? (
            <audio src={audioUrl} controls className="media-player audio-player" />
          ) : (
            <div className="media-placeholder">Готовы к записи</div>
          )
        )}
        {!audioUrl && isVideo && !isRecording && (
          <div className="media-overlay-placeholder">Предпросмотр появится при старте записи</div>
        )}
      </div>

      <div className="audio-recorder-controls">
        <div className="audio-control-wrapper">
          <button
            type="button"
            onClick={handleButtonClick}
            className={getButtonClass()}
          >
            {isRecording ? (
              <>
                <span className="stop-icon"></span>
                {getButtonText()}
              </>
            ) : (
              <>
                <span className="record-icon"></span>
                {getButtonText()}
              </>
            )}
          </button>
          
          <div className="recording-timer-wrapper">
            <span className="recording-time">{formatTime(recordingTime)}</span>
            <span className={`recording-dot ${isRecording ? 'recording-dot-active' : ''}`}></span>
          </div>
        </div>
        
        <div className="audio-recorder-playback">
          {audioUrl && recordingDuration < minDuration && (
            <div className="warning-inline">Минимальная длительность записи: {minDuration} секунд</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;

